import { useCurrentConsumerProject } from "@/lib/context/current-consumer-project-context";
import { useInterfacesTheme } from "lib/theme/ThemeProvider";
import { ComponentPropsWithoutRef } from "react";
import { cn } from "utils/cn";

type ChatbotWrapperProps = ComponentPropsWithoutRef<"div"> & {
  isEditing: boolean;
  isPopup?: boolean;
  style: React.CSSProperties;
};

export function ChatbotWrapper({
  children,
  isEditing,
  isPopup,
  style,
  ...props
}: ChatbotWrapperProps) {
  const Wrapper = isEditing ? EditingChatbotWrapper : LiveChatbotWrapper;
  const interfacesTheme = useInterfacesTheme();
  const { isHidden } = useCurrentConsumerProject();
  return (
    <div
      {...props}
      className={cn(
        "relative flex w-full flex-col items-stretch font-sans",
        {
          "text-zi-text": !interfacesTheme,
          "text-foreground": interfacesTheme,
          "min-h-svh": isHidden && !isPopup,
          "min-h-0": !isHidden || isPopup,
        },
        props.className
      )}
    >
      <Wrapper
        className={cn({ "min-h-svh": isHidden && !isPopup })}
        style={isHidden ? {} : style}
        isPopup={isPopup}
      >
        {children}
      </Wrapper>
    </div>
  );
}

function EditingChatbotWrapper({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isPopup,
  ...props
}: Omit<ChatbotWrapperProps, "isEditing">) {
  return (
    <div
      {...props}
      className={cn("flex flex-col justify-end gap-5", props.className)}
    />
  );
}

function LiveChatbotWrapper({
  isPopup,
  style,
  children,
  ...props
}: Omit<ChatbotWrapperProps, "isEditing">) {
  const interfacesTheme = useInterfacesTheme();

  return (
    <div
      {...props}
      style={isPopup ? undefined : style}
      className={cn(
        "flex h-full w-full flex-col justify-end",
        {
          "absolute gap-0": isPopup,
          "relative gap-1": !isPopup,
          "bg-zi-pageBackground": !interfacesTheme,
          "bg-background": interfacesTheme,
        },
        props.className
      )}
    >
      {children}
    </div>
  );
}
